import { useState, useEffect, useCallback } from 'react'
import type { BillCounterConfig, CounterData, BillCounterStatus } from '@abcpay/shared'
import {
  loadCounterSettings,
  startCounter as startCounterAPI,
  stopCounter as stopCounterAPI,
  getCounterStatus,
  onCounterData,
  onCounterStatus,
  onCounterError,
} from '../utils/counterManager'

interface UseBillCounterReturn {
  isConnected: boolean
  lastCounterData: CounterData | null
  status: BillCounterStatus | null
  error: string | null
  startCounter: (config?: BillCounterConfig) => Promise<void>
  stopCounter: () => Promise<void>
  refreshStatus: () => Promise<void>
  clearLastData: () => void
}

export function useBillCounter(): UseBillCounterReturn {
  const [isConnected, setIsConnected] = useState(false)
  const [lastCounterData, setLastCounterData] = useState<CounterData | null>(null)
  const [status, setStatus] = useState<BillCounterStatus | null>(null)
  const [error, setError] = useState<string | null>(null)

  const refreshStatus = useCallback(async () => {
    try {
      const nextStatus = await getCounterStatus()
      setStatus(nextStatus)
      setIsConnected(nextStatus.connected)
      if (nextStatus.error) {
        setError(nextStatus.error)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }, [])

  const startCounter = useCallback(async (config?: BillCounterConfig) => {
    try {
      const targetConfig = config ?? (await loadCounterSettings())
      const result = await startCounterAPI(targetConfig)
      setStatus(result)
      setIsConnected(result.connected)
      if (result.error) {
        setError(result.error)
      } else {
        setError(null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      setIsConnected(false)
    }
  }, [])

  const stopCounter = useCallback(async () => {
    try {
      await stopCounterAPI()
      setIsConnected(false)
      setStatus({ connected: false, port: '' })
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }, [])

  const clearLastData = useCallback(() => {
    setLastCounterData(null)
  }, [])

  useEffect(() => {
    const unsubscribeData = onCounterData((data) => {
      setLastCounterData(data)
      setError(null)
    })

    const unsubscribeStatus = onCounterStatus((nextStatus) => {
      setStatus(nextStatus)
      setIsConnected(nextStatus.connected)
      if (nextStatus.error) {
        setError(nextStatus.error)
      }
    })

    const unsubscribeError = onCounterError((message) => {
      setError(message)
      setIsConnected(false)
    })

    let cancelled = false
    const initCounter = async () => {
      try {
        const currentStatus = await getCounterStatus()
        if (cancelled) return
        setStatus(currentStatus)
        setIsConnected(currentStatus.connected)
        if (currentStatus.error) {
          setError(currentStatus.error)
        }

        const config = await loadCounterSettings()
        if (!cancelled && config.enabled && config.serialPort && !currentStatus.connected) {
          await startCounter(config)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err))
        }
      }
    }

    initCounter()

    return () => {
      cancelled = true
      unsubscribeData()
      unsubscribeStatus()
      unsubscribeError()
    }
  }, [startCounter])

  return {
    isConnected,
    lastCounterData,
    status,
    error,
    startCounter,
    stopCounter,
    refreshStatus,
    clearLastData,
  }
}
